import { useState, useCallback } from 'react';
import {
  Container, Typography, Box, Button, Paper, Stepper, Step, StepLabel,
  TextField, RadioGroup, FormControlLabel, Radio, FormLabel, FormControl,
  CircularProgress, Alert,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import BloodTypePicker, { type BloodType } from '../components/BloodTypePicker';
import CascadingDropdown from '../components/CascadingDropdown';

interface FormData {
  full_name: string;
  phone: string;
  birth_date: string;
  gender: 'male' | 'female' | '';
  blood_type: BloodType | null;
  last_donation: string;
  country: string;
  governorate: string;
  city: string;
}

const INITIAL: FormData = {
  full_name: '',
  phone: '+20',
  birth_date: '',
  gender: '',
  blood_type: null,
  last_donation: '',
  country: 'Egypt',
  governorate: '',
  city: '',
};

export default function RegisterPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<FormData>(INITIAL);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState('');

  const steps = [
    t('register.step_personal'),
    t('register.step_blood'),
    t('register.step_location'),
    t('register.step_confirm'),
  ];

  const set = (field: keyof FormData, value: string) => {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => ({ ...e, [field]: '' }));
  };

  const handleLocation = useCallback((country: string, governorate: string, city: string) => {
    setForm((f) => ({ ...f, country, governorate, city }));
    setErrors((e) => ({ ...e, governorate: '', city: '' }));
  }, []);

  const validate = (): boolean => {
    const e: Partial<Record<keyof FormData, string>> = {};
    if (step === 0) {
      if (!form.full_name.trim()) e.full_name = 'required';
      if (!/^\+20[0-9]{10}$/.test(form.phone)) e.phone = t('register.phone_hint');
      if (!form.birth_date) e.birth_date = 'required';
      if (!form.gender) e.gender = 'required';
    }
    if (step === 1) {
      if (!form.blood_type) e.blood_type = 'required';
    }
    if (step === 2) {
      if (!form.governorate) e.governorate = 'required';
      if (!form.city) e.city = 'required';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validate()) setStep((s) => s + 1); };
  const back = () => setStep((s) => s - 1);

  const submit = async () => {
    if (!validate()) return;
    setLoading(true);
    setServerError('');
    try {
      const apiBase = import.meta.env.VITE_API_BASE_URL ?? '';
      const res = await fetch(`${apiBase}/api/donors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          last_donation: form.last_donation || null,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.error ?? t('common.error'));
      setSubmitted(true);
    } catch (err: unknown) {
      setServerError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <Container maxWidth="sm" sx={{ py: 10, textAlign: 'center' }}>
        <CheckCircleOutlineIcon color="success" sx={{ fontSize: 96, mb: 2 }} />
        <Typography variant="h4" fontWeight={800} gutterBottom>
          {t('register.success_title')}
        </Typography>
        <Typography color="text.secondary" mb={4}>
          {t('register.success_body')}
        </Typography>
        <Button variant="contained" size="large" onClick={() => navigate('/')}>
          {t('register.back_home')}
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Typography variant="h4" fontWeight={800} mb={4} textAlign="center">
        {t('register.title')}
      </Typography>

      <Stepper activeStep={step} alternativeLabel sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}><StepLabel>{label}</StepLabel></Step>
        ))}
      </Stepper>

      <Paper sx={{ p: 4, borderRadius: 3 }}>
        {/* Step 0 — Personal */}
        {step === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              label={t('register.full_name')}
              value={form.full_name}
              onChange={(e) => set('full_name', e.target.value)}
              error={!!errors.full_name}
              helperText={errors.full_name}
            />
            <TextField
              label={t('register.phone')}
              value={form.phone}
              onChange={(e) => set('phone', e.target.value)}
              error={!!errors.phone}
              helperText={errors.phone || t('register.phone_hint')}
              inputProps={{ dir: 'ltr' }}
            />
            <TextField
              label={t('register.birth_date')}
              type="date"
              value={form.birth_date}
              onChange={(e) => set('birth_date', e.target.value)}
              error={!!errors.birth_date}
              helperText={errors.birth_date}
              InputLabelProps={{ shrink: true }}
            />
            <FormControl error={!!errors.gender}>
              <FormLabel>{t('register.gender')}</FormLabel>
              <RadioGroup
                row
                value={form.gender}
                onChange={(e) => set('gender', e.target.value)}
              >
                <FormControlLabel value="male" control={<Radio />} label={t('register.male')} />
                <FormControlLabel value="female" control={<Radio />} label={t('register.female')} />
              </RadioGroup>
            </FormControl>
          </Box>
        )}

        {/* Step 1 — Blood */}
        {step === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <BloodTypePicker
              label={t('register.blood_type')}
              value={form.blood_type}
              onChange={(v) => { setForm((f) => ({ ...f, blood_type: v })); setErrors((e) => ({ ...e, blood_type: '' })); }}
              error={errors.blood_type}
            />
            <TextField
              label={t('register.last_donation')}
              type="date"
              value={form.last_donation}
              onChange={(e) => set('last_donation', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        )}

        {/* Step 2 — Location */}
        {step === 2 && (
          <CascadingDropdown
            onChange={handleLocation}
            errors={{ governorate: errors.governorate, city: errors.city }}
          />
        )}

        {/* Step 3 — Confirm */}
        {step === 3 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {[
              ['register.full_name', form.full_name],
              ['register.phone', form.phone],
              ['register.birth_date', form.birth_date],
              ['register.gender', form.gender ? t(`register.${form.gender}`) : ''],
              ['register.blood_type', form.blood_type ?? ''],
              ['register.governorate', form.governorate],
              ['register.city', form.city],
            ].map(([label, val]) => (
              <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2" color="text.secondary">{t(label)}</Typography>
                <Typography variant="body2" fontWeight={600}>{val}</Typography>
              </Box>
            ))}
            {serverError && <Alert severity="error">{serverError}</Alert>}
          </Box>
        )}

        {/* Navigation */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button onClick={back} disabled={step === 0} variant="outlined">
            {t('register.back')}
          </Button>
          {step < steps.length - 1 ? (
            <Button onClick={next} variant="contained">
              {t('register.next')}
            </Button>
          ) : (
            <Button onClick={submit} variant="contained" disabled={loading} startIcon={loading ? <CircularProgress size={18} color="inherit" /> : null}>
              {t('register.submit')}
            </Button>
          )}
        </Box>
      </Paper>
    </Container>
  );
}
